import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { LanguageSelector } from './LanguageSelector';
import { Menu, X } from 'lucide-react';

export const Navbar = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/services', label: t('nav.services') },
    { path: '/pricing', label: t('nav.pricing') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center group"
            data-testid="logo-link"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_sepa-merchants-demo/artifacts/p11t5rmo_F7FB5692-8EC3-47F3-9917-09F08B4B4E2E%202.PNG" 
              alt="CentrixPay Logo"
              className="w-22 h-18 object-contain"
              style={{ width: '88px', height: '72px' }}
            />
            <span className="text-xl font-bold text-white group-hover:text-emerald-400 transition-colors font-montserrat -ml-1">
              CentrixPay
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-emerald-400'
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            <LanguageSelector />
            
            <Link
              to="/contact"
              className="hidden sm:flex items-center px-5 py-2.5 bg-emerald-500 text-black font-semibold text-sm rounded-full hover:bg-emerald-400 transition-all shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40"
              data-testid="cta-book-call"
            >
              {t('nav.bookCall')}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-white"
              data-testid="mobile-menu-button"
              aria-label="Toggle mobile menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div 
            className="lg:hidden py-4 border-t border-white/5 animate-in slide-in-from-top duration-200"
            data-testid="mobile-menu"
          >
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block py-3 px-4 text-base font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-emerald-400 bg-emerald-500/10'
                    : 'text-zinc-300 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 px-4">
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full text-center px-5 py-3 bg-emerald-500 text-black font-semibold rounded-full"
                data-testid="mobile-cta-book-call"
              >
                {t('nav.bookCall')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
